import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";

// Whitelist of allowed script sources for cookie consent providers
const ALLOWED_SCRIPT_DOMAINS = [
  'cookieyes.com',
  'cookiebot.com',
  'onetrust.com',
  'termly.io',
  'iubenda.com',
  'supabase.co', // For custom cookie consent on Supabase
];

function isAllowedScriptSrc(src: string): boolean {
  if (!src) return true; // Inline scripts without src are handled by DOMPurify
  try {
    const url = new URL(src);
    return ALLOWED_SCRIPT_DOMAINS.some(domain => url.hostname.endsWith(domain));
  } catch {
    return false;
  }
}

export function CookieConsentBanner() {
  const [injected, setInjected] = useState(false);

  useEffect(() => {
    const fetchAndInjectScript = async () => {
      if (injected) return;

      const { data } = await supabase
        .from("site_settings")
        .select("cookie_consent_enabled, cookie_consent_embed_code")
        .maybeSingle();

      if (!data?.cookie_consent_enabled || !data?.cookie_consent_embed_code) {
        return;
      }

      // Sanitize the embed code to prevent XSS
      const sanitizedHtml = DOMPurify.sanitize(data.cookie_consent_embed_code, {
        ADD_TAGS: ['script'],
        ADD_ATTR: ['data-website-id', 'data-supabase-url', 'async', 'defer'],
        FORCE_BODY: true,
      });

      // Create a container and parse the sanitized embed code
      const container = document.createElement("div");
      container.innerHTML = sanitizedHtml;

      // Extract and properly inject script tags by appending to DOM
      const scripts = container.querySelectorAll("script");
      scripts.forEach((script) => {
        const src = script.getAttribute('src');
        
        // Only allow scripts from whitelisted domains
        if (src && !isAllowedScriptSrc(src)) {
          console.warn('Cookie consent script blocked: source not in allowed list');
          return;
        }

        const newScript = document.createElement("script");
        
        // Copy only safe attributes (src, data-*, async, defer)
        const safeAttributes = ['src', 'async', 'defer'];
        Array.from(script.attributes).forEach((attr) => {
          if (safeAttributes.includes(attr.name) || attr.name.startsWith('data-')) {
            newScript.setAttribute(attr.name, attr.value);
          }
        });

        // If it has inline content, only allow if no external src (trusted inline from sanitized content)
        if (!src && script.textContent) {
          newScript.textContent = script.textContent;
        }

        // Append to document.head - this triggers proper execution
        document.head.appendChild(newScript);
      });

      setInjected(true);
    };

    fetchAndInjectScript();
  }, [injected]);

  return null;
}
